import React, { useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Promise } from 'bluebird';
import * as propz from 'propz';
import { School } from 'models/school';
import { SchoolLeague } from 'models/league';
import { Tournament } from 'models/tournament';
import { Championship } from 'models/championship';
import {
   SCHOOL_ROUTES,
   SCHOOL_UNION_ROUTES,
   SCHOOL_LEAGUE_ROUTES,
   TOURNAMENT_ROUTES,
   FOOTBALL_TOURNAMENT_ROUTES,
   TEAM_TOURNAMENT_V2_ROUTES,
   INDIVIDUAL_TOURNAMENT_V2_ROUTES,
   SCHOOL_CHALLENGE_ROUTES,
   NOT_AVAILABLE_SCHOOL_ROUTE
} from 'routing/routing';
import { SPORT_FORMAT } from 'consts/sport';
import { LAYOUT_TYPE } from 'consts/layout';
import { TOURNAMENT_TYPE } from 'consts/tournament';
import { KIND_SERVER_TO_CLIENT_MAPPING, PUBLIC_SCHOOL_STATUS } from 'consts/school';
import {
   getSchool,
   getSchoolById,
   getTournaments,
   checkSchoolPublicSitePassword,
   checkSchoolBigscreenPassword,
   checkTournamentPublicSitePassword,
   checkChampionshipPublicSitePassword,
   checkChampionshipBigscreenPassword,
   getChampionships,
   getSchoolLeagues
} from 'services/school';
import { getDomainWithoutWWW } from 'helpers/domain';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import Error from 'components/Error';
import Loader from 'components/loader';
import Layout from 'components/Layout';
import TournamentTVPage from 'views/TournamentTV/TournamentTV';
import TournamentProtectedAccess from 'components/TournamentProtectedAccess';
import { resolveSchoolPublicSite } from 'helpers/publicSite';
import SchoolBigScreen from 'views/SchoolBigScreen';
import UnionBigScreen from 'views/UnionBigScreen';
import SchoolWidget from 'views/SchoolWidget';
import {
   ChampionshipLiveLayout,
   ChampionshipOverview,
   ChampionshipTournament,
   ChampionshipTournamentEvent
} from 'views/ChampionshipLive';
import ChampionshipBigScreen from 'views/ChampionshipBigScreen';

const AppGlobalStyles = (
   <GlobalStyles
      styles={{
         'html, body': {
            height: '100%',
            margin: 0
         },
         '#root': {
            minHeight: '100%'
         }
      }}
   />
);

function App() {
   const [isLoading, setIsLoading] = useState<boolean>(true);
   const [error, setError] = useState<string>('');

   const [schools, setSchools] = useState<School[]>([]);
   const [schoolsUnion, setSchoolsUnion] = useState<School[]>([]);
   const [schoolLeagues, setSchoolLeagues] = useState<SchoolLeague[]>([]);
   const [tournaments, setTournaments] = useState<Tournament[]>([]);
   const [schoolOfEvent, setSchoolOfEvent] = useState<School>();

   const [schoolId, setSchoolId] = useState<string>('');
   const [tournamentId, setTournamentId] = useState<string>('');
   const [championship, setChampionship] = useState<Championship>();
   const [schoolLeagueId, setSchoolLeagueId] = useState<string>('');
   const [unlockedSchoolIds, setUnlockedSchoolIds] = useState<string[]>(() => {
      try {
         const saved = JSON.parse(sessionStorage.getItem('sit:unlocked-public-schools') || '[]');
         return Array.isArray(saved) ? saved : [];
      } catch {
         return [];
      }
   });
   const [unlockedTournamentIds, setUnlockedTournamentIds] = useState<string[]>(() => {
      try {
         const saved = JSON.parse(sessionStorage.getItem('sit:unlocked-public-tournaments') || '[]');
         return Array.isArray(saved) ? saved : [];
      } catch {
         return [];
      }
   });
   const [unlockedChampionshipIds, setUnlockedChampionshipIds] = useState<string[]>(() => {
      try {
         const saved = JSON.parse(sessionStorage.getItem('sit:unlocked-public-championships') || '[]');
         return Array.isArray(saved) ? saved : [];
      } catch {
         return [];
      }
   });

   const unlockSchool = (id: string) => {
      setUnlockedSchoolIds(current => {
         const next = current.includes(id) ? current : [...current, id];
         sessionStorage.setItem('sit:unlocked-public-schools', JSON.stringify(next));
         return next;
      });
   };

   const unlockTournament = (id: string) => {
      setUnlockedTournamentIds(current => {
         const next = current.includes(id) ? current : [...current, id];
         sessionStorage.setItem('sit:unlocked-public-tournaments', JSON.stringify(next));
         return next;
      });
   };

   const unlockChampionship = (id: string) => {
      setUnlockedChampionshipIds(current => {
         const next = current.includes(id) ? current : [...current, id];
         sessionStorage.setItem('sit:unlocked-public-championships', JSON.stringify(next));
         return next;
      });
   };

   const isSchoolOfEventExist = typeof schoolOfEvent !== 'undefined';
   const rawDomain: string = getDomainWithoutWWW();
   const isSchoolBigscreenDomain = rawDomain.startsWith('bigscreen_');
   const isTvDomain = rawDomain.startsWith('tv_') || isSchoolBigscreenDomain;
   const domain: string = isSchoolBigscreenDomain
      ? rawDomain.slice('bigscreen_'.length)
      : rawDomain.startsWith('tv_') ? rawDomain.slice(3) : rawDomain;

   useEffect(() => {
      setIsLoading(true);
      setError('');

      const promises = [
         getSchool(domain),
         getTournaments(domain),
         getChampionships(domain),
         getSchoolLeagues(domain),
      ];

      Promise.all(promises)
         .then(([schools, tournaments, championships, schoolLeagues]: any[]) => {
            const schoolsCount = schools.length;
            const tournamentsCount = tournaments.length;
            const championshipsCount = championships.length;
            const schoolLeaguesCount = schoolLeagues.length;
            const commonCount = schoolsCount + tournamentsCount + championshipsCount + schoolLeaguesCount;

            const isSchoolLeaguesExist = schoolLeaguesCount > 0;
            const isSchoolTournamentsExist = tournamentsCount > 0;

            switch (true) {
               case commonCount > 1:
                  console.error('Duplicate domain name for school, tournament or league');
                  setError('Duplicate domain name for school, tournament or league');

                  break;

               case schoolsCount === 1:
                  const [school] = schools;
                  const isSchoolUnion = school.kind === KIND_SERVER_TO_CLIENT_MAPPING.SCHOOL_UNION;

                  isSchoolUnion
                     ? setSchoolsUnion(schools)
                     : setSchools(schools);
                  
                  setSchoolId(school.id);

                  break;

               case tournamentsCount === 1:
                  const [tournament] = tournaments;
                  setTournamentId(tournament.id);
                  setTournaments(tournaments);

                  break;

               case championshipsCount === 1:
                  const [championship] = championships;
                  setChampionship(championship);

                  break;

               case schoolLeaguesCount === 1:
                  const [schoolLeague] = schoolLeagues;

                  setSchoolLeagues(schoolLeagues);
                  setSchoolLeagueId(schoolLeague.id);

                  break;

               case commonCount === 0:
                  console.error('Domain not exist');
                  setError('Domain not exist');

                  break;
            }

            switch (true) {
               case isSchoolLeaguesExist: {
                  const [ schoolLeague ] = schoolLeagues;
                  const { schoolId } = schoolLeague;
   
                  return getSchoolById(schoolId);
               }
            
               case isSchoolTournamentsExist: {
                  const [ tournament ] = tournaments;
                  const { schoolId } = tournament;
   
                  return getSchoolById(schoolId);
               }

               case championshipsCount === 1: {
                  const [championship] = championships;
                  return getSchoolById(championship.schoolId);
               }
            };
         })
         .then((schoolOfEvent) => {
            const isSchoolOfEventExist = typeof schoolOfEvent !== 'undefined';

            if (isSchoolOfEventExist) {
               setSchoolOfEvent(schoolOfEvent);
            }

            setIsLoading(false);
         })
         .catch((requestError) => {
            console.error('Unable to load public site', requestError);
            setError('We could not load this public site. Please refresh the page and try again.');
            setIsLoading(false);
         });
   }, [domain]);

   const getNotAvailableSchoolRoute = () => {
      const {
         path: notAvailableSchoolPath,
         index: notAvailableSchoolIndex,
         component: NotAvailableSchoolComponent
      } = NOT_AVAILABLE_SCHOOL_ROUTE;

      return (
         <Route
            path={notAvailableSchoolPath}
            index={notAvailableSchoolIndex}
            element={<NotAvailableSchoolComponent />}
         />
      );
   };

   const getRoutes = () => {
      const schoolsCount = schools.length;
      const schoolsUnionCount = schoolsUnion.length;
      const schoolLeaguesCount = schoolLeagues.length;
      const tournamentsCount = tournaments.length;
      const isChampionshipExist = Boolean(championship?.id);

      const isSchoolExist = schoolsCount > 0 && schools[0].kind === KIND_SERVER_TO_CLIENT_MAPPING.SCHOOL;
      const isSchoolUnionExist = schoolsUnionCount > 0;
      const isSchoolLeaguesExist = schoolLeaguesCount > 0;
      const isTournamentExist = tournamentsCount > 0;
      const schoolLeagueFirstItem = schoolLeagues[0];

      const isSchoolChallengeExist = schoolLeaguesCount > 0
         && schoolLeagueFirstItem.aggregationType === SPORT_FORMAT.INDIVIDUAL;

      switch (true) {
         case isSchoolOfEventExist && isChampionshipExist: {
            if (isTvDomain) {
               const status = propz.get(championship, ['publicBigscreenSite', 'status'], 'DISABLED');
               const protectedScreen = status === PUBLIC_SCHOOL_STATUS.PROTECTED;
               const unlockKey = `bigscreen:${championship!.id}`;
               const unlocked = unlockedChampionshipIds.includes(unlockKey);
               if (protectedScreen && !unlocked) return <Route path='*' element={<TournamentProtectedAccess resourceName={championship!.name} resourceType='championship' onSubmit={password => checkChampionshipBigscreenPassword(championship!.id, password)} onUnlocked={() => unlockChampionship(unlockKey)} />} />;
               return status === PUBLIC_SCHOOL_STATUS.PUBLIC_AVAILABLE || (protectedScreen && unlocked)
                  ? <Route path='*' element={<ChampionshipBigScreen championship={championship!} school={schoolOfEvent as School} />} />
                  : getNotAvailableSchoolRoute();
            }
            const publicSiteStatus = propz.get(championship, ['publicSite', 'status'], '');
            const isProtected = publicSiteStatus === PUBLIC_SCHOOL_STATUS.PROTECTED;
            const isUnlocked = unlockedChampionshipIds.includes(championship!.id);
            const isAvailable = publicSiteStatus === PUBLIC_SCHOOL_STATUS.PUBLIC_AVAILABLE || (isProtected && isUnlocked);

            if (isProtected && !isUnlocked) {
               return <Route path='*' element={<TournamentProtectedAccess
                  resourceName={championship!.name}
                  resourceType='championship'
                  onSubmit={password => checkChampionshipPublicSitePassword(championship!.id, password)}
                  onUnlocked={() => unlockChampionship(championship!.id)}
               />} />;
            }

            const isV2 = String(propz.get(championship, ['publicSite', 'templateVersion'], 'LEGACY')) === 'CHAMPIONSHIP_V2';
            if (!isAvailable || !isV2) return getNotAvailableSchoolRoute();

            return <Route path='/' element={<ChampionshipLiveLayout championship={championship!} school={schoolOfEvent as School} />}>
               <Route index element={<ChampionshipOverview championship={championship!} school={schoolOfEvent as School} />} />
               <Route path='tournaments/:tournamentId' element={<ChampionshipTournament championship={championship!} school={schoolOfEvent as School} />} />
               <Route path='tournaments/:tournamentId/event/:id' element={<ChampionshipTournamentEvent championship={championship!} school={schoolOfEvent as School} />} />
            </Route>;
         }

         case isSchoolExist: {
            const [school] = schools;

            if (window.location.pathname === '/widget') {
               // Website widgets are independent of the full public-site status.
               // Their data still follows the public API visibility and name rules.
               return <Route path='*' element={<SchoolWidget school={school} />} />;
            }

            if (isTvDomain) {
               const status = propz.get(school, ['publicBigscreenSite', 'status'], 'DISABLED');
               const protectedScreen = status === PUBLIC_SCHOOL_STATUS.PROTECTED;
               const unlocked = unlockedSchoolIds.includes(`bigscreen:${school.id}`);

               if (protectedScreen && !unlocked) {
                  return <Route path='*' element={<TournamentProtectedAccess
                     resourceName={school.name} resourceType='school big screen'
                     onSubmit={password => checkSchoolBigscreenPassword(school.id, password)}
                     onUnlocked={() => unlockSchool(`bigscreen:${school.id}`)} />} />;
               }
               return status === PUBLIC_SCHOOL_STATUS.PUBLIC_AVAILABLE || (protectedScreen && unlocked)
                  ? <Route path='*' element={<SchoolBigScreen school={school} />} />
                  : getNotAvailableSchoolRoute();
            }

            const publicSiteStatus = propz.get(school, ['publicSite', 'status'], '');
            const isSchoolProtected = publicSiteStatus === PUBLIC_SCHOOL_STATUS.PROTECTED;
            const isSchoolUnlocked = unlockedSchoolIds.includes(school.id);
            const isSchoolAvailable =
               publicSiteStatus === PUBLIC_SCHOOL_STATUS.PUBLIC_AVAILABLE ||
               (isSchoolProtected && isSchoolUnlocked);

            if (isSchoolProtected && !isSchoolUnlocked) {
               return (
                  <Route
                     path='*'
                     element={
                        <TournamentProtectedAccess
                           resourceName={school.name}
                           resourceType='school site'
                           onSubmit={password => checkSchoolPublicSitePassword(school.id, password)}
                           onUnlocked={() => unlockSchool(school.id)}
                        />
                     }
                  />
               );
            }

            const settings = resolveSchoolPublicSite(school);
            const isRouteEnabled = (path: string) => {
               switch (path) {
                  case '/': return true;
                  case '/calendar': return settings.pages.calendar;
                  case '/fixtures':
                  case '/results': return settings.pages.fixturesAndResults;
                  case '/tournaments': return settings.pages.tournaments;
                  case '/leagues': return settings.pages.leagues;
                  case '/challenges': return settings.pages.challenges;
                  case '/clubEvents': return settings.pages.clubEvents;
                  case '/news': return settings.pages.news;
                  case '/teams': return settings.pages.teams;
                  case '/records': return settings.pages.records;
                  case '/photos': return settings.pages.photos;
                  case '/search': return settings.pages.search;
                  default: return true;
               }
            };

            return isSchoolAvailable
               ? (
                  <Route path='/' element={<Layout school={school} type={LAYOUT_TYPE.SCHOOL} />}>
                     {SCHOOL_ROUTES.filter(route => isRouteEnabled(route.path)).map((route, index) => {
                        const {
                           component: RouteComponent,
                           index: routeIndex,
                           path: routePath
                        } = route;

                        return (
                           <Route
                              key={`route_${index}`}
                              path={routePath}
                              index={routeIndex}
                              element={<RouteComponent school={school} />}
                           />
                        )
                     })}
                  </Route>
               )
               : getNotAvailableSchoolRoute();
         }

         case isSchoolUnionExist: {
            const [schoolUnion] = schoolsUnion;

            if (window.location.pathname === '/widget') {
               return <Route path='*' element={<SchoolWidget school={schoolUnion} />} />;
            }

            if (isTvDomain) {
               const status = propz.get(schoolUnion, ['publicBigscreenSite', 'status'], 'DISABLED');
               const protectedScreen = status === PUBLIC_SCHOOL_STATUS.PROTECTED;
               const unlocked = unlockedSchoolIds.includes(`bigscreen:${schoolUnion.id}`);
               if (protectedScreen && !unlocked) {
                  return <Route path='*' element={<TournamentProtectedAccess resourceName={schoolUnion.name} resourceType='union big screen' onSubmit={password => checkSchoolBigscreenPassword(schoolUnion.id, password)} onUnlocked={() => unlockSchool(`bigscreen:${schoolUnion.id}`)} />} />;
               }
               return status === PUBLIC_SCHOOL_STATUS.PUBLIC_AVAILABLE || (protectedScreen && unlocked)
                  ? <Route path='*' element={<UnionBigScreen school={schoolUnion} />} />
                  : getNotAvailableSchoolRoute();
            }

            const publicSiteStatus = propz.get(schoolUnion, ['publicSite', 'status'], '');
            const isSchoolUnionProtected = publicSiteStatus === PUBLIC_SCHOOL_STATUS.PROTECTED;
            const isSchoolUnionUnlocked = unlockedSchoolIds.includes(schoolUnion.id);
            const isSchoolUnionAvailable = publicSiteStatus === PUBLIC_SCHOOL_STATUS.PUBLIC_AVAILABLE ||
               (isSchoolUnionProtected && isSchoolUnionUnlocked);

            if (isSchoolUnionProtected && !isSchoolUnionUnlocked) {
               return <Route path='*' element={<TournamentProtectedAccess
                  resourceName={schoolUnion.name}
                  resourceType='union site'
                  onSubmit={password => checkSchoolPublicSitePassword(schoolUnion.id, password)}
                  onUnlocked={() => unlockSchool(schoolUnion.id)}
               />} />;
            }

            const settings = resolveSchoolPublicSite(schoolUnion);
            const isUnionRouteEnabled = (path: string) => {
               switch (path) {
                  case '/calendar': return settings.pages.calendar;
                  case '/upcomingTournaments': return settings.pages.tournaments;
                  case '/tournamentResults': return settings.pages.tournamentResults;
                  case '/records': return settings.pages.records;
                  case '/memberSchools': return settings.pages.memberSchools;
                  case '/venues': return settings.pages.venues;
                  case '/photos': return settings.pages.photos;
                  case '/news': return settings.pages.news;
                  case '/files': return settings.pages.resources;
                  default: return true;
               }
            };

            return isSchoolUnionAvailable
               ? (
                  <Route path='/' element={<Layout school={schoolUnion} type={LAYOUT_TYPE.SCHOOL_UNION} />}>
                     {SCHOOL_UNION_ROUTES.filter(route => isUnionRouteEnabled(route.path)).map((route, index) => {
                        const {
                           component: RouteComponent,
                           index: routeIndex,
                           path: routePath
                        } = route;

                        return (
                           <Route
                              key={`route_${index}`}
                              path={routePath}
                              index={routeIndex}
                              element={<RouteComponent school={schoolUnion} />}
                           />
                        )
                     })}
                  </Route>
               )
               : getNotAvailableSchoolRoute();
         }

         case isSchoolOfEventExist && isSchoolLeaguesExist: {
            const [schoolLeague] = schoolLeagues;
            
            const publicSiteStatus = propz.get(schoolLeague, ['publicSite', 'status'], '');
            const isSchoolLeagueAvailable = publicSiteStatus === PUBLIC_SCHOOL_STATUS.PUBLIC_AVAILABLE;

            return isSchoolLeagueAvailable
               ? (
                  <Route
                     path='/'
                     element={<Layout
                           school={schoolOfEvent as School}
                           league={schoolLeague}
                           type={LAYOUT_TYPE.SCHOOL_LEAGUE}
                        />
                     }
                  >
                     {SCHOOL_LEAGUE_ROUTES.map((route, index) => {
                        const {
                           component: RouteComponent,
                           index: routeIndex,
                           path: routePath
                        } = route;

                        return (
                           <Route
                              key={`route_${index}`}
                              path={routePath}
                              index={routeIndex}
                              element={<RouteComponent school={schoolOfEvent} schoolLeague={schoolLeague} />}
                           />
                        )
                     })}
                  </Route>
               )
               : getNotAvailableSchoolRoute();
         }

         case isSchoolOfEventExist && isTournamentExist: {
            const [tournament] = tournaments;
            if (isTvDomain) {
              return (
               <Route
                  path='/'
                  element={<TournamentTVPage tournament={tournament} />}
                />
              );
            }

            const isTeamTournament = propz.get(tournament, ['type'], '') === TOURNAMENT_TYPE.TEAM_TOURNAMENT;
            const publicSiteStatus = propz.get(tournament, ['publicSite', 'status'], '');
            const isTournamentProtected = publicSiteStatus === PUBLIC_SCHOOL_STATUS.PROTECTED;
            const isTournamentUnlocked = unlockedTournamentIds.includes(tournament.id);
            const isTournamentAvailable =
              publicSiteStatus === PUBLIC_SCHOOL_STATUS.PUBLIC_AVAILABLE ||
              (isTournamentProtected && isTournamentUnlocked);

            if (isTournamentProtected && !isTournamentUnlocked) {
               return (
                  <Route
                     path='*'
                     element={
                        <TournamentProtectedAccess
                           resourceName={tournament.name}
                           resourceType='tournament'
                           onSubmit={password => checkTournamentPublicSitePassword(tournament.id, password)}
                           onUnlocked={() => unlockTournament(tournament.id)}
                        />
                     }
                  />
               );
            }

            const publicSiteTemplateVersion = String(
              propz.get(tournament, ['publicSite', 'templateVersion'], 'LEGACY')
            );
            const isTeamTournamentV2 = isTeamTournament &&
              publicSiteTemplateVersion === 'TEAM_TOURNAMENT_V2';
            const isIndividualTournamentV2 = !isTeamTournament &&
              publicSiteTemplateVersion === 'INDIVIDUAL_TOURNAMENT_V2';
            const routes = isTeamTournamentV2
              ? TEAM_TOURNAMENT_V2_ROUTES
              : isIndividualTournamentV2 ? INDIVIDUAL_TOURNAMENT_V2_ROUTES
              : isTeamTournament ? FOOTBALL_TOURNAMENT_ROUTES : TOURNAMENT_ROUTES;

            return isTournamentAvailable
               ? (
                  <Route
                     path='/'
                     element={<Layout
                           school={schoolOfEvent as School}
                           tournament={tournament}
                           type={isTeamTournament ? LAYOUT_TYPE.FOOTBALL_TOURNAMENT : LAYOUT_TYPE.TOURNAMENT}
                           />
                     }
                  >
                     {routes.map((route, index) => {
                        const {
                           component: RouteComponent,
                           index: routeIndex,
                           path: routePath
                        } = route;

                        return (
                           <Route
                              key={`route_${index}`}
                              path={routePath}
                              index={routeIndex}
                              element={<RouteComponent school={schoolOfEvent} tournament={tournament} />}
                           />
                        )
                     })}
                  </Route>
               )
               : getNotAvailableSchoolRoute();
         }

         case isSchoolOfEventExist && isSchoolChallengeExist: {
            const [schoolChallenge] = schoolLeagues;

            return (
               <Route
                  path='/'
                  element={<Layout
                        school={schoolOfEvent as School}
                        challenge={schoolChallenge}
                        type={LAYOUT_TYPE.SCHOOL_CHALLENGE}
                     />
                  }
               >
                  {SCHOOL_CHALLENGE_ROUTES.map((route, index) => {
                     const {
                        component: RouteComponent,
                        index: routeIndex,
                        path: routePath
                     } = route;

                     return (
                        <Route
                           key={`route_${index}`}
                           path={routePath}
                           index={routeIndex}
                           element={<RouteComponent school={schoolOfEvent} schoolChallenge={schoolChallenge} />}
                        />
                     )
                  })}
               </Route>
            );
         }
      }
   };

   if (error) {
      return (
         <Error text={error} />
      );
   }

   if (isLoading) {
      return (
         <Box sx={{ height: '100vh' }}>
            <Loader />
         </Box>
      );
   }

   return (
      <>
         {AppGlobalStyles}

         <Routes>
            {getRoutes()}
         </Routes>
      </>
   );
}

export default App;
